// Alerts of every module in one place: the bell in the top bar and the "Alerte" page (DMS deadlines, ALOP due dates,
// budget, items waiting …). The same alerts reach the user by a daily e-mail and, for the urgent ones, by the webhook.
import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { navigate } from '../store';
import { EmptyState, Floating, fmtDateTime, useToast } from '../ui';
import { Icon } from '../ui/Icon';

export interface Alert { id: number; module: string; kind: string; severity: 'INFO' | 'WARN' | 'HIGH'; title: string; text?: string | null; link?: string | null; createdAt: string; updatedAt: string; read: boolean }
interface AlertList { items: Alert[]; unread: number; high: number }

export const MODULE_LABEL: Record<string, string> = { DMS: 'Registratură', ALOP: 'ALOP' };
const KIND_LABEL: Record<string, string> = { DEADLINE: 'Termen', TASK: 'De făcut', DUE: 'Scadență', BUDGET: 'Buget', STALE: 'În așteptare', AL_END: 'Angajament', EINVOICE: 'e-Factura' };
const SEV_ICON: Record<string, string> = { HIGH: 'alert', WARN: 'clock', INFO: 'info' };

function useAlerts() {
  const [data, setData] = useState<AlertList | null>(null);
  const load = () => api.get<AlertList>('/api/alerts').then(setData).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    window.addEventListener('docflow:changed', load); window.addEventListener('alop:changed', load);
    return () => { clearInterval(t); window.removeEventListener('docflow:changed', load); window.removeEventListener('alop:changed', load); };
  }, []);
  const read = (id: number) => api.post<AlertList>(`/api/alerts/${id}/read`).then(setData).catch(() => {});
  const readAll = () => api.post<AlertList>('/api/alerts/read-all').then(setData).catch(() => {});
  return { data, load, read, readAll };
}

function AlertRow({ a, onOpen }: { a: Alert; onOpen: (a: Alert) => void }) {
  return (
    <button className={'alert-row sev-' + a.severity + (a.read ? ' read' : '')} onClick={() => onOpen(a)}>
      <Icon name={SEV_ICON[a.severity] || 'info'} size={16} />
      <div className="grow">
        <div className="alert-title">{a.title}</div>
        {a.text && <div className="muted small ellipsis">{a.text}</div>}
        <div className="alert-meta"><span className={'mod mod-' + a.module}>{MODULE_LABEL[a.module] || a.module}</span><span>{KIND_LABEL[a.kind] || a.kind}</span><span>{fmtDateTime(a.updatedAt)}</span></div>
      </div>
      {!a.read && <span className="dot" />}
    </button>
  );
}

/** The bell in the top bar: unread count (red when something is urgent) and the latest alerts. */
export function AlertsBell() {
  const { data, read, readAll } = useAlerts();
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const unread = Number(data?.unread || 0), high = Number(data?.high || 0);
  const go = (a: Alert) => { if (!a.read) read(a.id); setOpen(false); if (a.link) navigate(a.link); };
  return (
    <>
      <button ref={btn} className={'icon-btn bell' + (open ? ' on' : '')} title={unread ? `${unread} alerte necitite` : 'Alerte'} onClick={() => setOpen(o => !o)}>
        <Icon name="bell" />{unread > 0 && <span className={'bell-count' + (high > 0 ? ' high' : '')}>{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <Floating anchor={btn as React.RefObject<HTMLElement>} open className="alerts-pop" width={420} maxH={520} onMouseDown={e => e.stopPropagation()}>
          <div className="alerts-pop-head"><b>Alerte</b><span className="grow" />{unread > 0 && <button className="link-btn small" onClick={readAll}>Marchează toate ca citite</button>}</div>
          <div className="alerts-pop-list">
            {!data || data.items.length === 0 ? <div className="muted small pad">Nicio alertă. Aici apar termenele, scadențele, bugetul aproape epuizat și ce așteaptă după dumneavoastră.</div>
              : data.items.slice(0, 12).map(a => <AlertRow key={a.id} a={a} onOpen={go} />)}
          </div>
          <div className="alerts-pop-foot"><button className="btn sm ghost" onClick={() => { setOpen(false); navigate('/alerts'); }}>Toate alertele<Icon name="chevronRight" /></button></div>
        </Floating>
      )}
      {open && <div className="click-catcher" onMouseDown={() => setOpen(false)} />}
    </>
  );
}

/** The "Alerte" page: every alert of the user, by module. */
export function AlertsPage() {
  const toast = useToast();
  const { data, read, readAll } = useAlerts();
  const [mod, setMod] = useState('');
  const [onlyUnread, setOnlyUnread] = useState(false);
  const items = (data?.items || []).filter(a => (!mod || a.module === mod) && (!onlyUnread || !a.read));
  const mods = Array.from(new Set((data?.items || []).map(a => a.module)));
  const go = (a: Alert) => { if (!a.read) read(a.id); if (a.link) navigate(a.link); else toast('Alerta a fost marcată ca citită', 'ok'); };
  return (
    <div className="alop-page">
      <div className="toolbar">
        <div className="segmented">
          <button className={!mod ? 'active' : ''} onClick={() => setMod('')}>Toate <span className="n">{data?.items.length || 0}</span></button>
          {mods.map(m => <button key={m} className={mod === m ? 'active' : ''} onClick={() => setMod(m)}>{MODULE_LABEL[m] || m} <span className="n">{data!.items.filter(a => a.module === m).length}</span></button>)}
        </div>
        <label className="checkbox small"><input type="checkbox" checked={onlyUnread} onChange={e => setOnlyUnread(e.target.checked)} />Doar necitite</label>
        <span className="grow" />
        {Number(data?.unread || 0) > 0 && <button className="btn" onClick={readAll}><Icon name="check" />Marchează toate ca citite</button>}
      </div>
      {items.length === 0 ? <EmptyState icon="bell" title="Nicio alertă" text="Alertele apar aici, în clopoțelul din bara de sus și, o dată pe zi, într-un e-mail de rezumat." />
        : <div className="card"><div className="alerts-list">{items.map(a => <AlertRow key={a.id} a={a} onOpen={go} />)}</div></div>}
      <p className="muted small mt">Alertele dispar singure când situația se rezolvă (factura se plătește, documentul primește răspuns, bugetul se suplimentează). Urgentele pot fi trimise și prin SMS / Teams, prin webhook-ul configurat de administrator.</p>
    </div>
  );
}
